import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { FlexboxGrid, ButtonToolbar, Button } from 'rsuite';
import {
  get,
  has,
  map,
  head,
  join,
  omit,
  size,
  reduce,
  values,
  flatten,
  isEmpty
} from 'lodash/fp';
import { locationOptionValuesToLabels } from '../../../../../redux/Inventory/selectors/components/adjust-item-form';
import { getDonorSummaryReportSearchQuery } from '../../../../../redux/Inventory/selectors/components/reports/donor-summary';
import { getAll as getSummaryReportResult } from '../../../../../redux/Inventory/selectors/reports/donor-summary';
import { GroupByOptions } from '../../../../../components/inventory/reports/DonorSummarySearchForm/form-options';
import SearchDetails from '../../../../../components/inventory/reports/SearchDetails';
import ClientExportButton from '../../../../../components/ClientExportCsvButton';
import ReportTemplateFormModal from '../../../../../containers/ReportTemplateFormModal';
import { DONOR_SUMMARY } from '../../../../../helpers/ReportTemplateTypes';
import optionValuesToLabels from '../../../../../helpers/optionValuesToLabels';
import tableColumns from '../../../../../components/inventory/reports/DonorSummaryList/columns';

const reduceWithKey = reduce.convert({ cap: false });

export default function ResultHeader() {
  const [
    showReportTemplateFormModal,
    setShowReportTemplateFormModal
  ] = useState(false);
  const searchQuery = useSelector(getDonorSummaryReportSearchQuery);
  const searchResult = useSelector(getSummaryReportResult);
  const { createdAt, inventoryLocationId, groupBy, performedOn } = searchQuery;

  const dateRangeDetails = !isEmpty(performedOn)
    ? { label: 'Performed Date', value: join(' - ', performedOn) }
    : { label: 'Log Date', value: join(' - ', createdAt) };

  const searchDetails = [
    dateRangeDetails,
    {
      label: 'Inventory Locations',
      value: useSelector(state =>
        locationOptionValuesToLabels(state, inventoryLocationId)
      )
    },
    ...(!isEmpty(groupBy)
      ? [
          {
            label: 'Columns',
            value: optionValuesToLabels(groupBy, GroupByOptions)
          }
        ]
      : [])
  ];

  const resultColumns = useMemo(
    () => {
      if (!size(searchResult)) return [];

      const resultItem = head(searchResult);
      return flatten(
        values(
          reduceWithKey(
            (columns, column, key) =>
              has(key, resultItem) ? { ...columns, [key]: column } : columns,
            {},
            tableColumns
          )
        )
      );
    },
    [tableColumns, searchResult]
  );

  const exportColumns = useMemo(
    () => (size(resultColumns) ? map('label', resultColumns) : []),
    [resultColumns]
  );

  const exportData = useMemo(
    () => {
      if (!size(resultColumns)) return [];
      return map(
        row =>
          reduce(
            (rowData, { dataKey }) => [...rowData, get(dataKey, row)],
            [],
            resultColumns
          ),
        searchResult
      );
    },
    [resultColumns]
  );

  const handleOpenReportTemplateFormModal = () => {
    setShowReportTemplateFormModal(true);
  };

  const handleCloseReportTemplateFormModal = () => {
    setShowReportTemplateFormModal(false);
  };

  return (
    <FlexboxGrid justify="space-between">
      <FlexboxGrid.Item colspan={19}>
        <SearchDetails data={searchDetails} />
      </FlexboxGrid.Item>
      <FlexboxGrid.Item colspan={5}>
        <ButtonToolbar style={{ textAlign: 'right' }}>
          <Button
            appearance="ghost"
            onClick={handleOpenReportTemplateFormModal}
          >
            Save Preset
          </Button>
          <ClientExportButton
            filename={`donor_summary_report_${moment().format('YMMDD')}`}
            headerData={searchDetails}
            columns={exportColumns}
            data={exportData}
          />
        </ButtonToolbar>
        <ReportTemplateFormModal
          fileType={DONOR_SUMMARY}
          file={omit(['createdAt', 'performedOn'], searchQuery)}
          show={showReportTemplateFormModal}
          onSubmitted={handleCloseReportTemplateFormModal}
          handleClose={handleCloseReportTemplateFormModal}
        />
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
}
